"use client";

import { StorageManager } from "@/lib/storage/local-storage";

/**
 * Restorable sidebar surface modes.
 *
 * Only modes that can be reconstructed without hidden in-memory data belong in
 * the persisted sidebar session contract.
 */
export type AiSidePanelViewMode = "history" | "thread";

/**
 * Workspace-scoped sidebar session data that is safe to restore after reload.
 */
export type StoredAiSidePanelState = {
  chatId: string | null;
  viewMode: AiSidePanelViewMode;
};

const AI_SIDE_PANEL_STATE_STORAGE_VERSION = 1;

export const EMPTY_AI_SIDE_PANEL_STATE: StoredAiSidePanelState = {
  chatId: null,
  viewMode: "history",
};

export const BLANK_DRAFT_AI_SIDE_PANEL_STATE: StoredAiSidePanelState = {
  chatId: null,
  viewMode: "thread",
};

const isViewMode = (value: unknown): value is AiSidePanelViewMode => {
  return value === "history" || value === "thread";
};

const isStoredAiSidePanelState = (
  value: unknown,
): value is StoredAiSidePanelState => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return (
    "chatId" in value &&
    (typeof value.chatId === "string" || value.chatId === null) &&
    "viewMode" in value &&
    isViewMode(value.viewMode)
  );
};

const getStorageKey = (workspaceId: string) => {
  return `ai-side-panel-state:v${AI_SIDE_PANEL_STATE_STORAGE_VERSION}:${workspaceId}`;
};

const normalizeStoredAiSidePanelState = (
  state: unknown,
): StoredAiSidePanelState | null => {
  if (!isStoredAiSidePanelState(state)) {
    return null;
  }

  return state;
};

/**
 * Read the recoverable sidebar session for the active workspace.
 *
 * Only reconstructable UI intent belongs here. That includes a server-backed
 * chat as well as the explicit "blank new chat" thread surface, but excludes
 * ephemeral draft content because unsent messages are not persisted.
 */
export const readStoredAiSidePanelState = (
  workspaceId: string,
): StoredAiSidePanelState | null => {
  const storageKey = getStorageKey(workspaceId);
  const normalizedState = normalizeStoredAiSidePanelState(
    StorageManager.getJSON(storageKey),
  );

  if (!normalizedState) {
    StorageManager.removeItem(storageKey);
    return null;
  }

  return normalizedState;
};

/**
 * Persist the recoverable sidebar session for the active workspace.
 *
 * Callers must only pass reconstructable state. The helper validates the shape
 * and preserves the exact recoverable intent so reloads can reopen either
 * history, a real chat thread, or an intentional blank draft thread.
 */
export const writeStoredAiSidePanelState = (
  workspaceId: string,
  state: StoredAiSidePanelState,
) => {
  StorageManager.setJSON(
    getStorageKey(workspaceId),
    normalizeStoredAiSidePanelState(state) ?? EMPTY_AI_SIDE_PANEL_STATE,
  );
};

/**
 * Remove any recoverable sidebar session for the active workspace.
 */
export const clearStoredAiSidePanelState = (workspaceId: string) => {
  StorageManager.removeItem(getStorageKey(workspaceId));
};
